import React, { useMemo } from "react";
import { Typography, Box } from "@mui/material";
import styles from "./AverageLengthOfStayCard.module.scss";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { ADMISSION_CARDS_LABELS } from "../../../../types/admission.type";
import { calculateDiffBetweenPercentages, pickComparisonColor } from "../../../../utilis/percentage-comparison";
import { itemPercentage } from "../../../../utilis/common";
import ColorBox from "../../../../components/shared/dashboard/ColorBox";

const AverageLengthOfStayCard = ({
	data,
	comparisonData,
	loading,
	loadingComparison,
	filter,
	filterComparison,
	comparingAgainstDifferentFacility,
	admissionCompareAgainst,
	admissionCompareAgainstComparison,
	reverseColorsAdmissionPage,
	isMobile = false
}) => {
	// Get the average value from data
	const averageData = Array.isArray(data) && data.length > 0 ? data[0] : null;
	const averageValue = averageData?.total ?? 0;
	const averageValueComparison = Array.isArray(comparisonData) && comparisonData.length > 0 ? comparisonData[0]?.total : null;

	// Format the value with one decimal place
	const formattedValue = averageValue > 0 ? averageValue.toFixed(1) : "0";

	// Calculate comparison metrics for ColorBox
	// For Admission page, we need to convert values to percentages first (like DayCardList)
	const comparisonMetrics = useMemo(() => {
		if (!averageValueComparison || loading || loadingComparison || !averageValue || !filter || !filterComparison) {
			return {
				comparisonColor: null,
				comparingAgainstScaled: null,
				numberOfDays: null
			};
		}

		// For Admission page, convert to percentages using admissionCompareAgainst (like DayCardList)
		const valueForAdmission = itemPercentage(averageValue, admissionCompareAgainst, "number");
		const valueForAdmissionComparison = itemPercentage(
			averageValueComparison,
			admissionCompareAgainstComparison,
			"number"
		);

		// Use calculateDiffBetweenPercentages with the percentage values (like DayCardList)
		const {
			percentageDiff,
			itemTotalComparisonScaled,
			numberOfDays
		} = calculateDiffBetweenPercentages(
			valueForAdmission, // First value (current) - percentage
			valueForAdmissionComparison, // Second value (comparison) - percentage
			reverseColorsAdmissionPage, // reverseColorsAdmissionPage (like DayCardList)
			filter,
			filterComparison,
			averageValueComparison, // Pass original comparison value (not percentage) - like DayCardList
			null // projectionDays
		);

		const isSpecialPage = true;
		let comparisonColor = pickComparisonColor(percentageDiff, isSpecialPage);

		if (comparingAgainstDifferentFacility) {
			comparisonColor = null;
		}

		return {
			comparisonColor,
			comparingAgainstScaled: itemTotalComparisonScaled,
			numberOfDays
		};
	}, [averageValue, averageValueComparison, filter, filterComparison, loading, loadingComparison, comparingAgainstDifferentFacility, admissionCompareAgainst, admissionCompareAgainstComparison, reverseColorsAdmissionPage]);

	return (
		<div className={styles.averageLengthOfStayCard}>
			<div className={styles.header}>
				<Typography variant="body1" className={styles.title}>
					{ADMISSION_CARDS_LABELS.averageStayDaysData}
				</Typography>
				<Typography variant="caption" className={styles.subtitle}>
					For the selected date range
				</Typography>
			</div>
			
			<div className={styles.mainContent}>
				<div className={styles.valueCircle}>
					{loading ? (
						<div className={styles.loadingContainer}>
							<span className="skeleton-text" style={{ fontSize: '42px', fontWeight: 700 }}>0.0</span>
							<span className="skeleton-text" style={{ fontSize: '14px', marginTop: '4px' }}>Days</span>
						</div>
					) : (
						<div className={styles.valueContent}>
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
								{comparisonMetrics.comparisonColor && (
									<ColorBox
										color={comparisonMetrics.comparisonColor}
										sx={{ width: "8px", height: "30px", mr: "3px" }}
										comparingAgainst={averageValueComparison}
										comparingAgainstScaled={comparisonMetrics.comparingAgainstScaled}
										numberOfDays={comparisonMetrics.numberOfDays}
										filter={filter}
										loading={loading}
										loadingComparison={loadingComparison}
										customLabel="Average Length Of Stay"
										hideProjection={true}
									/>
								)}
								<Typography 
									variant="h3" 
									className={styles.value}
									style={{ color: '#000000' }}
								>
									{formattedValue}
								</Typography>
							</Box>
							<Typography variant="body2" className={styles.unit}>
								Days
							</Typography>					
						</div>
					)}
					<CalendarMonthIcon className={styles.calendarIcon} />
				</div>
			</div>
		</div>
	);
};

export default AverageLengthOfStayCard;

